import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Bell, LogOut, MapPin, Search } from "lucide-react";
import VisionLogo from "@/components/VisionLogo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const baseNav = [
  { to: "/dashboard", label: "Dashboard", testid: "nav-dashboard" },
  { to: "/records",   label: "Records",   testid: "nav-records" },
];

const LocationSwitcher = () => {
  const { isAdmin, selectedLocationId, setSelectedLocation } = useAuth();
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    if (!isAdmin) return;
    // _skipScope: get the full active-Locations list, unfiltered.
    api
      .get("/locations", { params: { only_active: true }, _skipScope: true })
      .then((r) => setLocations(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [isAdmin]);

  if (!isAdmin) return null;

  return (
    <div
      className="hidden items-center gap-2 rounded-full border bg-white px-3 py-1.5 md:flex"
      style={{ borderColor: "rgba(52,73,94,0.14)" }}
      data-testid="header-location-switcher-wrap"
    >
      <MapPin className="h-4 w-4" style={{ color: "var(--vision-orange)" }} />
      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-vision-slate/60">
        Scope
      </span>
      <select
        className="vision-select bg-transparent text-sm font-semibold text-vision-slate outline-none"
        data-testid="header-location-switcher"
        value={selectedLocationId || ""}
        onChange={(e) => setSelectedLocation(e.target.value || null)}
        aria-label="Location scope"
      >
        <option value="">All Locations</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id} label={l.name}>
            {l.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export const AppHeader = () => {
  const navigate = useNavigate();
  const { user, isAdmin, isLocationAdmin, isSupervisor, isAsstSupervisor, logout } = useAuth();

  const items = [...baseNav];
  if (isSupervisor || isAsstSupervisor || isAdmin || isLocationAdmin) {
    items.push({ to: "/attendance", label: "Attendance", testid: "nav-attendance" });
    items.push({ to: "/sessions", label: "Sessions", testid: "nav-sessions" });
  }
  if (isSupervisor || isAdmin || isLocationAdmin)
    items.push({ to: "/allocations", label: "Allocation", testid: "nav-allocation" });
  if (isSupervisor || isAdmin || isLocationAdmin)
    items.push({ to: "/report", label: "Report", testid: "nav-report" });
  if (isAdmin || isLocationAdmin)
    items.push({ to: "/masters", label: "Masters", testid: "nav-masters" });

  const initials = (user?.username || "??")
    .split(/[.\s@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");

  return (
    <header
      className="sticky top-0 z-30 w-full border-b bg-white/85 backdrop-blur"
      style={{ borderColor: "rgba(52,73,94,0.10)" }}
      data-testid="app-header"
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-4 px-6">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center"
          data-testid="app-header-logo"
        >
          <VisionLogo size={30} tone="color" />
        </button>

        <nav className="ml-4 hidden items-center gap-1 md:flex" data-testid="app-header-nav">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `chip ${
                  isActive
                    ? "bg-vision-navy text-white border border-transparent"
                    : "bg-transparent text-vision-slate border border-transparent hover:bg-[color:var(--vision-ice)]"
                }`
              }
              style={{ padding: "8px 14px" }}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <LocationSwitcher />
          {isLocationAdmin && (
            <div
              className="hidden items-center gap-2 rounded-full border bg-white px-3 py-1.5 md:flex"
              style={{ borderColor: "rgba(52,73,94,0.14)" }}
              data-testid="header-location-pill"
            >
              <MapPin className="h-4 w-4" style={{ color: "var(--vision-orange)" }} />
              <span className="text-sm font-semibold text-vision-slate">
                {user?.location_name || "Location"}
              </span>
            </div>
          )}

          <button
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-[color:var(--vision-ice)]"
            data-testid="app-header-notifications"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5 text-vision-slate" />
            <span
              className="absolute right-2 top-2 h-2 w-2 rounded-full"
              style={{ backgroundColor: "var(--vision-orange)" }}
            />
          </button>

          <button
            type="button"
            onClick={() => navigate("/profile")}
            className="ml-1 flex items-center gap-2 rounded-full border px-2 py-1 hover:bg-[color:var(--vision-ice)] transition"
            style={{ borderColor: "rgba(52,73,94,0.14)" }}
            data-testid="app-header-user"
            title="My Profile"
            aria-label="Open my profile"
          >
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
              style={{ backgroundColor: "var(--vision-navy)" }}
            >
              {initials || "U"}
            </div>
            <div className="hidden pr-2 leading-tight md:block text-left">
              <div className="max-w-[140px] truncate text-sm font-semibold text-vision-slate">
                {user?.username || "—"}
              </div>
              <div className="text-[11px] text-vision-slate/60">{user?.role || ""}</div>
            </div>
          </button>

          <Button
            data-testid="app-header-logout"
            variant="ghost"
            size="icon"
            className="rounded-full text-vision-slate hover:bg-[color:var(--vision-ice)]"
            onClick={() => {
              logout();
              navigate("/login", { replace: true });
            }}
            aria-label="Logout"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
